import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardCheck,
  PlayCircle,
  AlertTriangle,
  GraduationCap,
  ShieldAlert,
  CheckCircle2,
  Gauge,
  Trophy,
  Timer,
  AlertCircle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import GamificationCard from "@/components/GamificationCard";
import { Fingerprint, MapPin } from "lucide-react";
import { ChecklistRunnerBody } from "@/pages/ChecklistRunner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const PRIORITY_TONE = {
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  medium: "bg-blue-100 text-blue-800 border-blue-200",
  low: "bg-stone-100 text-stone-700",
};

// Item 3 — blocos separados de checklists pendentes por tipo de atribuição
const ASSIGNMENT_GROUPS = [
  { key: "individual", title: "Checklists individuais" },
  { key: "role", title: "Checklists do perfil" },
  { key: "sector", title: "Checklists do setor" },
  { key: "general", title: "Checklists gerais" },
];

/**
 * FASE 17 — operator/auditor personal dashboard: pending checklists of the day,
 * today's non-conformities, pending trainings + score chart, visible PCCs.
 */
const PUNCH_LABEL = {
  entry: "Entrada",
  break_start: "Saída int.",
  break_end: "Volta int.",
  exit: "Saída",
};
const PUNCH_ORDER = ["entry", "break_start", "break_end", "exit"];

// Widget rápido de ponto no dashboard do operador
function PontoWidget() {
  const [st, setSt] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api
      .get("/timeclock/my-status")
      .then(({ data }) => setSt(data))
      .catch(() => setSt(null));
  }, []);
  if (!st) return null;
  const byType = Object.fromEntries((st.records || []).map((r) => [r.type, r]));
  const dayDone = !st.next_type;
  return (
    <Card className="p-5 rounded-xl border-stone-200 bg-white" data-testid="my-day-ponto">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-mono">
            Meu ponto hoje
          </div>
          <div className="font-display text-lg font-bold tracking-tight mt-0.5">
            {dayDone ? "Jornada completa" : `Próxima: ${st.next_label || "batida"}`}
          </div>
        </div>
        <Button
          onClick={() => navigate("/timeclock")}
          className={`h-11 px-5 text-white ${
            dayDone ? "bg-stone-400 hover:bg-stone-500" : "bg-emerald-800 hover:bg-emerald-900"
          }`}
          data-testid="my-day-punch-btn"
        >
          <Fingerprint className="h-5 w-5 mr-2" /> {dayDone ? "Ver ponto" : "Bater ponto"}
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-4">
        {PUNCH_ORDER.map((t) => {
          const r = byType[t];
          const isNext = st.next_type === t;
          return (
            <div
              key={t}
              className={`rounded-lg border p-2 text-center ${
                r
                  ? "border-emerald-200 bg-emerald-50"
                  : isNext
                  ? "border-emerald-600 bg-white ring-1 ring-emerald-600"
                  : "border-stone-200 bg-stone-50"
              }`}
            >
              <div className="text-[9px] uppercase tracking-wide text-stone-500 font-mono leading-tight">
                {PUNCH_LABEL[t]}
              </div>
              <div className="font-display font-bold text-sm mt-0.5 tabular-nums">
                {r
                  ? new Date(r.timestamp).toLocaleTimeString("pt-BR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : isNext
                  ? "agora"
                  : "—"}
              </div>
            </div>
          );
        })}
      </div>
      {st.schedule && (
        <div className="text-[11px] text-stone-500 font-body mt-3 inline-flex items-center gap-1.5">
          <MapPin className="h-3 w-3" />
          Jornada {st.schedule.entry_time}–{st.schedule.exit_time} · tolerância{" "}
          {st.schedule.tolerance_minutes} min
        </div>
      )}
    </Card>
  );
}

export default function MyDay() {
  const [data, setData] = useState(null);
  const [runExec, setRunExec] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = () =>
    api
      .get("/dashboard/my-day")
      .then(({ data }) => setData(data))
      .catch((err) => console.error("my-day failed", err));

  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <Card className="p-6 rounded-xl border-stone-200 bg-white text-sm text-stone-500 font-body">
        Carregando seu dia...
      </Card>
    );
  }

  const pending = data.pending_executions || [];
  const nonconf = data.non_conformities || [];
  const pendingTrainings = data.pending_trainings || [];
  const scores = (data.training_scores || []).map((s) => ({
    name: (s.title || "").slice(0, 18) || s.training_id.slice(0, 6),
    score: s.score,
  }));
  const pccs = data.pccs || [];
  const punct = data.punctuality || null;
  const scoreColor =
    punct?.score === null || punct?.score === undefined
      ? "text-stone-400"
      : punct.score >= 80
      ? "text-emerald-700"
      : punct.score >= 50
      ? "text-amber-600"
      : "text-rose-600";

  return (
    <div className="space-y-6" data-testid="my-day">
      {/* Execução rápida em popup */}
      <Dialog open={!!runExec} onOpenChange={(o) => !o && setRunExec(null)}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="my-day-run-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {runExec?.template_name || "Checklist"}
            </DialogTitle>
            <DialogDescription className="font-body text-xs text-stone-500">
              Execução rápida · responda e finalize sem sair do dashboard
            </DialogDescription>
          </DialogHeader>
          {runExec && (
            <ChecklistRunnerBody
              executionId={runExec.id}
              onExit={(done) => {
                setRunExec(null);
                if (done) load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* FASE 20 — gamification */}
      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <PontoWidget />
        <GamificationCard />
      </div>

      {/* FASE 19 — personal punctuality qualification (oculto p/ operador) */}
      {punct && user?.role !== "operator" && (
        <Card className="p-5 rounded-xl border-stone-200 bg-white" data-testid="my-day-punctuality">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
              <Gauge className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
                Indicadores operacionais
              </div>
              <h3 className="font-display text-lg font-bold tracking-tight">
                Minha qualificação (30 dias)
              </h3>
            </div>
            <div className="text-center px-3">
              <div className={`font-display text-3xl font-bold ${scoreColor}`} data-testid="my-day-score">
                {punct.score === null || punct.score === undefined ? "—" : punct.score}
              </div>
              <div className="text-[10px] text-stone-500 font-body uppercase tracking-wider">Pontualidade</div>
            </div>
            <div className="flex gap-4 text-center">
              <div>
                <div className="font-display text-xl font-bold text-emerald-700 flex items-center gap-1 justify-center">
                  <Trophy className="h-4 w-4" /> {punct.on_time}
                </div>
                <div className="text-[10px] text-stone-500 font-body uppercase">No prazo</div>
              </div>
              <div>
                <div className="font-display text-xl font-bold text-amber-600 flex items-center gap-1 justify-center">
                  <Timer className="h-4 w-4" /> {punct.late}
                </div>
                <div className="text-[10px] text-stone-500 font-body uppercase">Atrasados</div>
              </div>
              <div>
                <div className="font-display text-xl font-bold text-rose-600 flex items-center gap-1 justify-center">
                  <AlertCircle className="h-4 w-4" /> {punct.missed}
                </div>
                <div className="text-[10px] text-stone-500 font-body uppercase">Não executados</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Pending checklists — hoje, separados por tipo de atribuição */}
      <section>
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
            Meu dia
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            Checklists pendentes do dia
          </h3>
        </div>
        {pending.length === 0 ? (
          <Card className="rounded-xl border-stone-200 bg-white" data-testid="my-day-pending">
            <div className="p-6 text-center text-sm text-stone-500 font-body flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nenhum checklist pendente. Bom trabalho!
            </div>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="my-day-pending">
            {ASSIGNMENT_GROUPS.map((g) => {
              const items = pending.filter(
                (e) => (e.assignment_type || "general") === g.key
              );
              if (items.length === 0) return null;
              return (
                <div key={g.key} data-testid={`my-day-group-${g.key}`}>
                  <div className="text-xs font-body font-semibold text-stone-500 uppercase tracking-wider mb-1.5">
                    {g.title} · {items.length}
                  </div>
                  <Card className="rounded-xl border-stone-200 bg-white divide-y divide-stone-200">
                    {items.map((e) => (
            <div
              key={e.id}
              className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                e.overdue ? "bg-rose-50/70 hover:bg-rose-50" : "hover:bg-stone-50"
              }`}
              onClick={() => setRunExec(e)}
              data-testid={`my-day-exec-${e.id}`}
            >
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                e.overdue ? "bg-rose-100 text-rose-700" : "bg-emerald-50 text-emerald-800"
              }`}>
                <ClipboardCheck className="h-4 w-4" />
              </div>
              <div
                className={`shrink-0 text-center rounded-lg px-2.5 py-1 ${
                  e.overdue ? "bg-rose-600 text-white" : "bg-emerald-800 text-white"
                }`}
                data-testid={`my-day-time-${e.id}`}
              >
                <div className="text-[8px] font-body uppercase tracking-wider opacity-80 leading-none">
                  Horário
                </div>
                <div className="font-display font-extrabold text-lg leading-tight tabular-nums">
                  {new Date(e.created_at).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-display font-semibold truncate ${e.overdue ? "text-rose-700" : "text-stone-950"}`}>
                  {e.template_name || "Checklist"}
                </div>
                <div className={`text-xs font-body ${e.overdue ? "text-rose-600" : "text-stone-500"}`}>
                  Criado em {formatDateTime(e.created_at)}
                </div>
              </div>
              {e.overdue && (
                <Badge className="bg-rose-600 text-white border-none" data-testid={`my-day-overdue-${e.id}`}>
                  <AlertTriangle className="h-3 w-3 mr-1" /> Atrasado
                </Badge>
              )}
              <Badge
                variant="outline"
                className={e.status === "in_progress" ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-stone-100 text-stone-700"}
              >
                {e.status === "in_progress" ? "Em andamento" : "Pendente"}
              </Badge>
              <Button
                size="sm"
                className={e.overdue ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-800 hover:bg-emerald-900 text-white"}
                onClick={(e2) => {
                  e2.stopPropagation();
                  setRunExec(e);
                }}
                data-testid={`my-day-run-${e.id}`}
              >
                <PlayCircle className="h-3.5 w-3.5 mr-1" />
                {e.status === "in_progress" ? "Continuar" : "Executar"}
              </Button>
            </div>
                    ))}
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Non-conformities generated today */}
      {nonconf.length > 0 && (
        <Card className="p-5 rounded-xl border-amber-300 bg-amber-50/60" data-testid="my-day-nonconf">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-amber-200/70 text-amber-900 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold tracking-tight">
                {nonconf.length} não conformidade(s) gerada(s) hoje
              </h3>
              <div className="mt-3 space-y-2">
                {nonconf.map((o) => (
                  <div
                    key={o.id}
                    className="rounded-md bg-white border border-amber-200 px-3 py-2 flex items-center gap-2"
                  >
                    <div className="font-display font-semibold text-sm truncate flex-1">
                      {o.title}
                    </div>
                    <Badge variant="outline" className={PRIORITY_TONE[o.priority] || ""}>
                      {o.priority === "critical" ? "Crítica" : o.priority === "high" ? "Alta" : o.priority === "medium" ? "Média" : "Baixa"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Trainings */}
      <section className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 rounded-xl border-stone-200 bg-white" data-testid="my-day-trainings">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="h-4 w-4 text-emerald-700" />
            <h3 className="font-display font-bold tracking-tight">Treinamentos pendentes</h3>
          </div>
          {pendingTrainings.length === 0 ? (
            <div className="text-sm text-stone-500 font-body py-4 text-center">
              Você está em dia com os treinamentos.
            </div>
          ) : (
            <div className="space-y-2">
              {pendingTrainings.map((t) => (
                <Link
                  key={t.id}
                  to="/trainings"
                  className="flex items-center gap-2 rounded-md border border-stone-200 px-3 py-2 hover:border-emerald-300 transition-colors"
                  data-testid={`my-day-training-${t.id}`}
                >
                  <span className="font-display font-semibold text-sm flex-1 truncate">{t.title}</span>
                  <Badge className="bg-amber-500 text-white border-none text-[10px]">Pendente</Badge>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 rounded-xl border-stone-200 bg-white" data-testid="my-day-scores">
          <div className="flex items-center gap-2 mb-3">
            <GraduationCap className="h-4 w-4 text-emerald-700" />
            <h3 className="font-display font-bold tracking-tight">Minhas notas de treinamento</h3>
          </div>
          {scores.length === 0 ? (
            <div className="text-sm text-stone-500 font-body py-4 text-center">
              Nenhum quiz respondido ainda.
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scores} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v}%`, "Nota"]} />
                  <Bar dataKey="score" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </section>

      {/* Visible PCCs */}
      {pccs.length > 0 && (
        <section>
          <div className="mb-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
                APPCC
              </div>
              <h3 className="font-display text-xl font-bold tracking-tight">PCCs do seu perfil</h3>
            </div>
            <Link to="/appcc" className="text-sm font-body text-emerald-800 hover:underline">
              Registrar monitoramento
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-day-pccs">
            {pccs.map((p) => (
              <Card key={p.id} className="p-4 rounded-xl border-stone-200 bg-white">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-emerald-700 shrink-0" />
                  <div className="font-display font-semibold text-sm truncate">{p.name}</div>
                </div>
                <div className="text-xs text-stone-500 font-body mt-1">
                  Limite: {p.control_limit_min ?? "—"} a {p.control_limit_max ?? "—"} {p.unit}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
